// client/src/components/admin/QuotaHarvesting.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody,
  LinearProgress, Switch, Collapse,
  TextField, Paper, Snackbar, Divider
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useNavigate } from 'react-router-dom';
import AppBarHeader from '../AppBarHeader';
import axiosInstance from '../../axiosInstance';
import { jwtDecode } from 'jwt-decode';

function relativeTime(dateStr) {
  if (!dateStr) return '—';
  const diff = new Date(dateStr) - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.floor(abs / 60000);
  const hours = Math.floor(abs / 3600000);
  if (mins < 2) return diff > 0 ? 'in <1 min' : '<1 min ago';
  if (mins < 60) return diff > 0 ? `in ${mins}m` : `${mins}m ago`;
  return diff > 0 ? `in ${hours}h` : `${hours}h ago`;
}

const HARVEST_INTERVAL_MS = 60 * 60 * 1000; // default 60-min interval for free APIs

function StatusChip({ row }) {
  if (row.isHarvesting) return <Chip label="Harvesting" color="warning" size="small" />;
  if (!row.harvestingEnabled) return <Chip label="Disabled" color="default" size="small" />;
  if (row.resetAt) {
    // Quota-based API: in window when reset is within 60 min
    const msUntilReset = new Date(row.resetAt) - Date.now();
    if (msUntilReset > 0 && msUntilReset < 60 * 60 * 1000) {
      return <Chip label="In Window" color="success" size="small" />;
    }
  } else {
    // Free/unlimited API: in window when last harvest was > 60 min ago (or never run)
    const lastHarvest = row.lastHarvestAt ? new Date(row.lastHarvestAt).getTime() : 0;
    if (Date.now() - lastHarvest >= HARVEST_INTERVAL_MS) {
      return <Chip label="In Window" color="success" size="small" />;
    }
  }
  return <Chip label="Idle" color="info" size="small" />;
}

export default function QuotaHarvesting() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const isAdmin = user?.isAdmin || false;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [overrideInputs, setOverrideInputs] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [barcodeStatus, setBarcodeStatus] = useState(null);
  const [enrichRunning, setEnrichRunning] = useState({ music: false, lego: false });
  const [enrichLimit, setEnrichLimit] = useState({ music: '50', lego: '100' });

  const loadBarcodeStatus = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/barcode-enrich/status');
      setBarcodeStatus(res.data);
    } catch { /* non-critical */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/quota-harvesting/status');
      setRows(res.data);
    } catch (err) {
      setError('Failed to load quota status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) { navigate('/dashboard'); return; }
    load();
    loadBarcodeStatus();
    const timer = setInterval(() => { load(); loadBarcodeStatus(); }, 30000);
    return () => clearInterval(timer);
  }, [isAdmin, navigate, load, loadBarcodeStatus]);

  const handleToggleEnabled = async (apiName, currentValue) => {
    try {
      await axiosInstance.patch(`/quota-harvesting/${apiName}/enabled`, { enabled: !currentValue });
      setSnackbar({ open: true, message: `${apiName} harvesting ${!currentValue ? 'enabled' : 'disabled'}`, severity: 'success' });
      load();
    } catch {
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  };

  const handleSaveOverride = async (apiName) => {
    const inputs = overrideInputs[apiName] || {};
    const body = {};
    if (inputs.limit) body.limit = parseInt(inputs.limit, 10);
    if (inputs.throttleMs) body.throttleMs = parseInt(inputs.throttleMs, 10);
    if (inputs.harvestWindowMinutes) body.harvestWindowMinutes = parseInt(inputs.harvestWindowMinutes, 10);
    try {
      await axiosInstance.post(`/quota-harvesting/${apiName}/override`, body);
      setSnackbar({ open: true, message: 'Override saved', severity: 'success' });
      load();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save override', severity: 'error' });
    }
  };

  const handleClearOverride = async (apiName) => {
    try {
      await axiosInstance.delete(`/quota-harvesting/${apiName}/override`);
      setSnackbar({ open: true, message: 'Override cleared', severity: 'success' });
      load();
    } catch {
      setSnackbar({ open: true, message: 'Failed to clear override', severity: 'error' });
    }
  };

  const handleRun = async (apiName) => {
    try {
      const res = await axiosInstance.post(`/quota-harvesting/${apiName}/run`);
      setSnackbar({ open: true, message: `${apiName} harvest started (budget: ${res.data.budget} requests)`, severity: 'success' });
      setTimeout(load, 1000);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to start harvest', severity: 'error' });
    }
  };

  const handleCancel = async (apiName) => {
    try {
      await axiosInstance.post(`/quota-harvesting/${apiName}/cancel`);
      setSnackbar({ open: true, message: `${apiName} cancellation requested`, severity: 'info' });
      setTimeout(load, 1000);
    } catch {
      setSnackbar({ open: true, message: 'Failed to request cancellation', severity: 'error' });
    }
  };

  const handleResetLock = async (apiName) => {
    try {
      await axiosInstance.post(`/quota-harvesting/${apiName}/reset-lock`);
      setSnackbar({ open: true, message: `${apiName} lock reset — harvester can run again`, severity: 'success' });
      setTimeout(load, 500);
    } catch {
      setSnackbar({ open: true, message: 'Failed to reset lock', severity: 'error' });
    }
  };

  const handleEnrichRun = async (type) => {
    setEnrichRunning(r => ({ ...r, [type]: true }));
    try {
      const limit = Math.max(1, Math.min(500, parseInt(enrichLimit[type], 10) || 50));
      await axiosInstance.post('/admin/barcode-enrich/run', { type, limit });
      const note = type === 'music'
        ? `Music barcode enrichment started (${limit} entries, ~${limit}s at MusicBrainz rate limit)`
        : `LEGO barcode enrichment started (${limit} entries via Brickset)`;
      setSnackbar({ open: true, message: note, severity: 'success' });
      setTimeout(loadBarcodeStatus, 3000);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to start enrichment', severity: 'error' });
    } finally {
      setEnrichRunning(r => ({ ...r, [type]: false }));
    }
  };

  const handleLogout = () => { localStorage.removeItem('token'); navigate('/login'); };

  if (loading) {
    return (
      <>
        <AppBarHeader isAdmin={isAdmin} userId={user?.id} onLogout={handleLogout} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      </>
    );
  }

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={user?.id} onLogout={handleLogout} />
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
        <Button variant="outlined" onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
          ← Back to Admin Dashboard
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CloudDownloadIcon color="secondary" />
          <Typography variant="h5">Quota Harvesting</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>API</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Resets</TableCell>
                <TableCell>Last Harvest</TableCell>
                <TableCell>Catalogue</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Overrides</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <React.Fragment key={row.apiName}>
                  <TableRow>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {row.apiName}
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Box>
                        <Typography variant="caption">{row.used} / {row.limit}</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={row.limit > 0 ? Math.min(100, (row.used / row.limit) * 100) : 0}
                          sx={{ mt: 0.5 }}
                          color={row.used / row.limit > 0.9 ? 'error' : 'primary'}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{relativeTime(row.resetAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.lastHarvestAt ? (
                        <Box>
                          <Typography variant="caption">{relativeTime(row.lastHarvestAt)}</Typography>
                          {row.lastHarvestStats?.stored > 0 && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              +{row.lastHarvestStats.stored} stored
                            </Typography>
                          )}
                        </Box>
                      ) : <Typography variant="caption" color="text.secondary">Never</Typography>}
                    </TableCell>
                    <TableCell>
                      {Object.entries(row.catalogueSizes || {}).map(([type, count]) => (
                        <Typography key={type} variant="caption" display="block">
                          {type}: {count.toLocaleString()}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell><StatusChip row={row} /></TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={row.harvestingEnabled}
                        onChange={() => handleToggleEnabled(row.apiName, row.harvestingEnabled)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          disabled={row.isHarvesting}
                          onClick={() => handleRun(row.apiName)}
                        >
                          Run
                        </Button>
                        {row.isHarvesting ? (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => handleCancel(row.apiName)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleResetLock(row.apiName)}
                              title="Force-release a stuck lock (use if Cancel doesn't work)"
                            >
                              Reset Lock
                            </Button>
                          </>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setExpanded(e => ({ ...e, [row.apiName]: !e[row.apiName] }))}
                      >
                        {expanded[row.apiName] ? 'Close' : 'Overrides'}
                      </Button>
                    </TableCell>
                  </TableRow>

                  {/* Override panel */}
                  <TableRow>
                    <TableCell colSpan={9} sx={{ p: 0 }}>
                      <Collapse in={!!expanded[row.apiName]}>
                        <Box sx={{ p: 2, bgcolor: 'action.hover', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <TextField
                            size="small"
                            label="Quota limit override"
                            type="number"
                            value={overrideInputs[row.apiName]?.limit || ''}
                            onChange={e => setOverrideInputs(prev => ({ ...prev, [row.apiName]: { ...prev[row.apiName], limit: e.target.value } }))}
                            sx={{ width: 160 }}
                          />
                          <TextField
                            size="small"
                            label="Throttle ms"
                            type="number"
                            value={overrideInputs[row.apiName]?.throttleMs || ''}
                            onChange={e => setOverrideInputs(prev => ({ ...prev, [row.apiName]: { ...prev[row.apiName], throttleMs: e.target.value } }))}
                            sx={{ width: 130 }}
                          />
                          <TextField
                            size="small"
                            label="Harvest window (min)"
                            type="number"
                            value={overrideInputs[row.apiName]?.harvestWindowMinutes || ''}
                            onChange={e => setOverrideInputs(prev => ({ ...prev, [row.apiName]: { ...prev[row.apiName], harvestWindowMinutes: e.target.value } }))}
                            sx={{ width: 170 }}
                          />
                          <Button variant="contained" size="small" onClick={() => handleSaveOverride(row.apiName)}>
                            Save
                          </Button>
                          <Button variant="outlined" size="small" color="error" onClick={() => handleClearOverride(row.apiName)}>
                            Clear
                          </Button>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" variant="body2">
                      No quota data yet — starts populating when search routes are called.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        {/* Barcode Enrichment */}
        <Paper sx={{ mt: 3, p: 3 }}>
          <Typography variant="h6" gutterBottom>Barcode Enrichment</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Looks up EAN/UPC barcodes for harvested catalogue items so barcode scans can be served
            locally without hitting external APIs. Books use ISBN-13 directly (no enrichment needed).
            Music uses MusicBrainz (1 req/sec). LEGO uses Brickset (requires API key — register free at brickset.com/api).
          </Typography>

          {[
            {
              type: 'music',
              label: 'Music (MusicBrainz)',
              description: 'Rate-limited to 1 req/sec — 50 entries ≈ 50 seconds. Runs automatically every 4 hours.',
              defaultLimit: '50',
              maxLimit: 500,
            },
            {
              type: 'lego',
              label: 'LEGO (Brickset)',
              description: 'Requires a Brickset API key in API Keys settings. 100 entries ≈ 20 seconds. Runs automatically every 4 hours.',
              defaultLimit: '100',
              maxLimit: 500,
            },
          ].map(({ type, label, description, maxLimit }) => {
            const s = barcodeStatus?.[type];
            const pct = s?.total > 0 ? Math.round((s.checked / s.total) * 100) : 0;
            return (
              <Box key={type} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{description}</Typography>
                {s ? (
                  <>
                    <Box sx={{ display: 'flex', gap: 4, mb: 1, flexWrap: 'wrap' }}>
                      {[
                        { label: 'Total', value: s.total },
                        { label: 'Checked', value: s.checked },
                        { label: 'With barcode', value: s.withBarcode },
                        { label: 'Pending', value: s.pending },
                      ].map(({ label: l, value }) => (
                        <Box key={l}>
                          <Typography variant="caption" color="text.secondary">{l}</Typography>
                          <Typography variant="h6">{value.toLocaleString()}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary">{pct}% checked</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{ mt: 0.5, mb: 1.5, height: 8, borderRadius: 4 }}
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Loading…</Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    size="small"
                    label="Batch size"
                    type="number"
                    value={enrichLimit[type]}
                    onChange={e => setEnrichLimit(l => ({ ...l, [type]: e.target.value }))}
                    inputProps={{ min: 1, max: maxLimit }}
                    sx={{ width: 120 }}
                  />
                  <Button
                    variant="contained"
                    disabled={enrichRunning[type] || s?.pending === 0}
                    onClick={() => handleEnrichRun(type)}
                  >
                    {enrichRunning[type] ? 'Starting…' : 'Run Now'}
                  </Button>
                  {s?.pending === 0 && <Chip label="All entries checked" color="success" size="small" />}
                </Box>
                <Divider sx={{ mt: 3 }} />
              </Box>
            );
          })}
        </Paper>

        {/* External-only APIs — no local catalogue harvesting */}
        <Paper sx={{ mt: 3, p: 3 }}>
          <Typography variant="h6" gutterBottom>External-Only APIs</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These collection types use external APIs on-demand only. No local catalogue harvesting is configured.
          </Typography>
          {[
            { label: 'Comics', apis: 'ComicVine API (requires API key)' },
            { label: 'Trading Cards (Pokémon)', apis: 'Pokémon TCG API (requires API key — search only, no background harvest)' },
          ].map(({ label, apis }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography variant="subtitle2">{label}</Typography>
                <Typography variant="caption" color="text.secondary">{apis}</Typography>
              </Box>
              <Chip label="No harvesting" size="small" color="default" />
            </Box>
          ))}
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
